// Generated by Claude Code
import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { AlertTriangle } from "lucide-react";
import { State, useCustomState } from "#core/state/State.ts";
import { ApiError, apiErrorDetail } from "#core/api/Api.ts";
import PasswordResetApi from "#core/api/auth/PasswordResetApi.ts";
import appLogo from "#assets/images/app-logo.png";

export const Route = createFileRoute("/_authentication/forgot-password")({
  component: RouteComponent,
});

class ForgotPasswordState extends State<ForgotPasswordState> {
  email = "";
  validated = false;
  submitting = false;
  sent = false;
  error = "";

  async requestReset() {
    this.reactTo.submitting = true;
    this.reactTo.error = "";
    try {
      await new PasswordResetApi().requestReset(this.email);
      this.reactTo.sent = true;
    } catch (e) {
      this.reactTo.error =
        e instanceof ApiError && e.response?.status === 429
          ? i18n.t("forgotPasswordPage.rateLimited", { ns: "auth" })
          : apiErrorDetail(e, i18n.t("forgotPasswordPage.failed", { ns: "auth" }));
    }
    this.reactTo.submitting = false;
  }
}

function RouteComponent() {
  const formRef = useRef<HTMLFormElement | null>(null);
  const state = useCustomState(() => new ForgotPasswordState());
  const { t } = useTranslation("auth");

  const submitRequest = () => {
    state.validated = true;
    if (formRef.current && !formRef.current.checkValidity()) {
      return;
    }
    void state.requestReset();
  };

  return (
    <div className="container ynt-fullscreen-center">
      <div className="w-100" style={{ maxWidth: "440px" }}>
        <div className="card border-0 rounded-0 shadow-lg">
          <div className="ynt-flush-card-header">
            <div>
              <img src={appLogo} alt="YNTEQ" style={{ maxHeight: "40px" }} className="img-fluid" />
            </div>
          </div>

          <div className="card-body p-4 p-md-5">
            {state.sent ? (
              <>
                <h5 className="mb-2">{t("forgotPasswordPage.sentTitle")}</h5>
                <p className="text-body-secondary mb-0">{t("forgotPasswordPage.sentMessage")}</p>
              </>
            ) : (
              <>
                <h5 className="mb-2">{t("forgotPasswordPage.title")}</h5>
                <p className="text-body-secondary mb-4">{t("forgotPasswordPage.intro")}</p>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    submitRequest();
                  }}
                  ref={formRef}
                  className={state.validated ? "was-validated" : ""}
                  noValidate
                >
                  <div className="mb-3">
                    <label htmlFor="resetEmailInput" className="form-label">
                      {t("shared.fields.email")}
                    </label>
                    <input
                      type="email"
                      value={state.email}
                      onChange={(e) => {
                        state.email = e.target.value;
                      }}
                      className="form-control"
                      id="resetEmailInput"
                      autoComplete="email"
                      required
                    />
                    <div className="invalid-feedback">{t("emailValidation")}</div>
                  </div>

                  <div className="alert alert-warning d-flex align-items-start gap-2" role="alert">
                    <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
                    <span>{t("forgotPasswordPage.warning")}</span>
                  </div>

                  {state.error && <div className="text-danger mb-3">{state.error}</div>}

                  <button
                    type="submit"
                    className="btn btn-primary ynt-full-width-center-btn"
                    disabled={state.submitting}
                  >
                    {state.submitting ? (
                      <>
                        {t("forgotPasswordPage.submitting")}
                        <div className="ms-2 spinner-border spinner-border-sm"></div>
                      </>
                    ) : (
                      t("forgotPasswordPage.submit")
                    )}
                  </button>
                </form>
              </>
            )}

            <div className="text-center mt-4">
              <Link className="text-orange text-decoration-none" to="/login">
                {t("backToLogin")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
